import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Bundle,
  ITEM_KIND_LABEL,
  ITEM_STATUS,
  ITEM_STATUS_LABEL,
  formatMoney,
  readRequest,
  startCheckout,
} from "services/marketplace.service";

/**
 * Where a family's request stands. Reached by its reference only; keep the link to come back.
 * Once every provider has answered, the family pays for what was accepted, once, through Stripe.
 */
const RequestStatusPage = () => {
  const { reference } = useParams();
  const [bundle, setBundle] = React.useState<Bundle | null>(null);
  const [state, setState] = React.useState<"loading" | "found" | "missing">("loading");
  const [searchParams] = useSearchParams();
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState<string | null>(null);

  const pay = async () => {
    if (!bundle) return;
    setPaying(true);
    setPayError(null);
    const { data: url, error } = await startCheckout(bundle.reference);
    if (error || !url) {
      setPayError(error?.message ?? "Payment could not be started. Try again in a moment.");
      setPaying(false);
      return;
    }
    window.location.href = url;
  };

  React.useEffect(() => {
    if (!reference) {
      setState("missing");
      return;
    }
    (async () => {
      const { data, error } = await readRequest(reference);
      if (error || !data) setState("missing");
      else {
        setBundle(data);
        setState("found");
      }
    })();
  }, [reference]);

  return (
    <main className="min-h-screen bg-stone-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <a href="/marketplace" className="text-lg font-semibold text-slate-800">
            Funely
          </a>
        </div>
      </header>
      <section className="mx-auto max-w-3xl px-6 py-12">
        {state === "loading" && <p className="text-slate-500">Loading…</p>}
        {state === "missing" && (
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">Request not found</h1>
            <p className="text-slate-600">The link may not be complete.</p>
          </div>
        )}
        {state === "found" && bundle && (
          <article>
            <h1 className="text-3xl font-semibold text-slate-900 mb-2">Your request has been sent</h1>
            <p className="text-slate-600 mb-8">
              Each provider will reply to {bundle.contactEmail || bundle.contactPhone}. Keep this page's
              link to see where things stand.
            </p>
            <ul className="flex flex-col gap-3">
              {bundle.items.map((item) => (
                <li key={item.itemId} className="rounded-[10px] border border-slate-200 bg-white p-5">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{ITEM_KIND_LABEL[item.kind]}</p>
                  <p className="text-base font-medium text-slate-900">{item.providerName}</p>
                  <p className="text-sm text-slate-600">{item.description}</p>
                  <p className="mt-2 text-sm font-medium text-slate-800">
                    {ITEM_STATUS_LABEL[item.status]}
                    {item.status === ITEM_STATUS.ACCEPTED && item.monetaryAmount > 0 && (
                      <> · {formatMoney(item.monetaryAmount, item.monetaryCurrency)}</>
                    )}
                    {item.refunded && <> · refunded to you</>}
                  </p>
                </li>
              ))}
            </ul>
            <Payment
              bundle={bundle}
              justPaid={searchParams.get("paid") === "1"}
              paying={paying}
              error={payError}
              onPay={pay}
            />
          </article>
        )}
      </section>
    </main>
  );
};

type PaymentProps = {
  bundle: Bundle;
  justPaid: boolean;
  paying: boolean;
  error: string | null;
  onPay: () => void;
};

/** What the family owes, and the one payment that covers it. */
const Payment = ({ bundle, justPaid, paying, error, onPay }: PaymentProps) => {
  const accepted = bundle.items.filter((i) => i.status === ITEM_STATUS.ACCEPTED && i.monetaryAmount > 0);
  const waiting = bundle.items.some((i) => i.status === ITEM_STATUS.REQUESTED);
  if (bundle.paidOn) {
    return <p className="mt-8 rounded-[10px] bg-emerald-50 p-5 text-sm text-emerald-900">Paid. Thank you.</p>;
  }
  if (justPaid) {
    return (
      <p className="mt-8 rounded-[10px] bg-emerald-50 p-5 text-sm text-emerald-900">
        Thank you. Your payment is being confirmed; this page will show it shortly.
      </p>
    );
  }
  if (waiting || accepted.length === 0) return null;
  const currency = accepted[0].monetaryCurrency;
  const total = accepted.reduce((sum, i) => sum + i.monetaryAmount, 0);
  return (
    <div className="mt-8 rounded-[10px] border border-slate-200 bg-white p-5">
      <p className="text-sm text-slate-600">Everyone has answered. One payment covers it all.</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{formatMoney(total, currency)}</p>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <button
        type="button"
        onClick={onPay}
        disabled={paying}
        className="mt-4 rounded-md bg-slate-800 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
      >
        {paying ? "Opening payment…" : `Pay ${formatMoney(total, currency)}`}
      </button>
    </div>
  );
};

export default RequestStatusPage;
